import React, { useEffect, useRef, useState } from "react"
import {
  DataGrid,
  GridColDef,
  GridEditInputCell,
  GridSelectionModel,
  GridToolbar,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@mui/x-data-grid"
import type { GridCellEditCommitParams } from "@mui/x-data-grid"
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro"
import { RiExpandUpDownLine } from "react-icons/ri"
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { Box, FormControl, Grid, IconButton, LinearProgress, MenuItem, Select, SelectChangeEvent, Button, Menu, useMediaQuery, useTheme } from "@mui/material"
import useAuth from "../../context/AuthProvider"
import moment from "moment"
import { ClientBillingReportModel } from "../../models/ClientBillingReportModel"
// import * as XLSX from 'xlsx'
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Swal from "sweetalert2"
// import * as XLSX from 'xlsx-js-style';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { style } from "@mui/system"
import formatPrice from "../../helper/currencyFormatter"
import { formatUtcToLocal } from "../../helper/utils"
import { config } from "../../Constants"
import { AppFlags } from "../../helper/config"
import { apiClient } from "../../helper/api"
import { printPDFReport } from "./printPDFReport"
import { PrintData, GrpData } from "./PrintData"
import { KeyboardArrowUp } from "@mui/icons-material"
import { KeyboardArrowDown } from "@mui/icons-material"

// @ts-ignore
import html2pdf from "html2pdf.js";
import { jsPDF } from 'jspdf'
import { PartnerType } from "../../models/PartnersModel"
import { writeExcel_ClientBilling } from "../../helper/xlsFunctions"
import { buildReportDocumentFilename } from "../../helper/reportDocumentFilename"
import {
  billableFilingRowsForMebUi,
  buildMonthEndBillingLineSignature,
  normalizeFeeDueOverrideForApi,
  parseFeeDueToNumber,
  rowsForFiledTransactionsExport,
  userCanSaveMonthEndBillingFeeOverride,
} from "../../helper/monthEndBillingFeeOverride"

/** Month End Billing Date Filed: parse API values (e.g. MM-dd-yyyy) without Invalid Date / NaN in UI. */
function formatBillingDateFiled(dateValue: unknown): string {
  if (dateValue == null || dateValue === "") return "";
  const formatted = formatUtcToLocal(String(dateValue), "MM/DD/YYYY", true);
  return formatted === "N/A" ? "" : formatted;
}

function getBillingPeriodDisplayDates(year: number, month: number): { startDate: string; endDate: string } {
  const mm = String(month).padStart(2, "0");
  const yyyy = String(year);

  if (month === 3) {
    return { startDate: `03/01/${yyyy}`, endDate: `03/27/${yyyy}` };
  }

  if (month === 4) {
    return { startDate: `03/28/${yyyy}`, endDate: `04/30/${yyyy}` };
  }

  const lastDay = new Date(year, month, 0).getDate();
  const dd = String(lastDay).padStart(2, "0");
  return { startDate: `${mm}/01/${yyyy}`, endDate: `${mm}/${dd}/${yyyy}` };
}

/** Fee Due inline editor: right-aligned (RTL-style placement for currency); `direction: ltr` keeps digit order correct. */
function FeeDueGridEditInputCell(props: React.ComponentProps<typeof GridEditInputCell>) {
  return (
    <GridEditInputCell
      {...props}
      debounceMs={0}
      sx={{
        justifyContent: "flex-end",
        "& .MuiInputBase-input": {
          textAlign: "right",
          direction: "ltr",
        },
        ...props.sx,
      }}
    />
  );
}

export enum BillingReportNames {
  UpcomingStateReport = 'Upcoming State Reports and Payments',
  OutstandingStateReport = 'Outstanding State Reports and Payments',
  CompletedStateReport = 'Completed State Reports and Payments'
}


interface Iprops {
  dataLoading: boolean
  listData: ClientBillingReportModel[]
  onRemoveItems: (val: ClientBillingReportModel[]) => void
  setItemList: React.Dispatch<React.SetStateAction<ClientBillingReportModel[]>>
  selectedMonth: number
  handleFilterChange: any
  handleSortChange: any
  selectedPeriod: string
  selectedYear: string
  selectedPartner: PartnerType
  sortModel: any
  setSortModel: any
  billingCompanyRollup?: number
}

export const ClientBillingReportMonthlyList: React.FC<Iprops> = ({
  dataLoading,
  listData,
  onRemoveItems,
  setItemList,
  selectedMonth,
  handleFilterChange,
  handleSortChange,
  selectedPeriod,
  selectedYear,
  selectedPartner,
  sortModel,
  setSortModel,
  billingCompanyRollup = 1,
}) => {


  /**
   * Grid + Filed Transactions Excel/CSV: billable filing rows only (`NeedDiligentSearch` '').
   * Diligent Search queue rows (TRUE/FALSE) bill DiligentSearchFee and belong on the DS report, not this export.
   * Comprehensive ZIP `customerPolicyIds` still use full `listData`.
   */
  const mebGridRows = (rows: ClientBillingReportModel[]) => billableFilingRowsForMebUi(rows);
  const { user } = useAuth()
  const token = user?.token
  const [feeOverrideSaving, setFeeOverrideSaving] = useState(false)
  const [selectedRows, setSelectedRows] = useState<ClientBillingReportModel[]>()
  const defaultRowsForExport = (rows: ClientBillingReportModel[]) =>
    rowsForFiledTransactionsExport(rows, selectedRows);
  //const dataToShow = listData.filter((item) => (!item.needDiligentSearch))
  //const dataToShow = listData.filter((item) => (item.needDiligentSearch === "" && item.transactionType === "" && item.policyExpDate === "" && item.policyEffDate === ""  && item.grossPremium === ""))

  const [_pageSize, setPageSize] = React.useState(25);
  const [loading, setloading] = useState(false)
  const theme = useTheme();
  const isTabletOrDown = useMediaQuery(theme.breakpoints.down('md'));
  const clientBillingGridApiRef = useGridApiRef();

  const onRowsSelectionHandler = (ids: GridSelectionModel) => {
    const selectedIDs = new Set(ids)
    const visible = mebGridRows(listData)
    const selectedRowData = visible.filter((row) => selectedIDs.has(row?.rowNum ?? ""))
    setSelectedRows(selectedRowData)
  }

  const handleDelete = () => {
    if (!selectedRows || selectedRows.length < 1) return
    onRemoveItems(selectedRows)
    setSelectedRows([])
  }
  const getBase64ImageFromUrl = (url: string) => {
    return new Promise<string>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      };
      img.onerror = () => {
        reject(new Error('Failed to load image.'));
      };
      img.src = url;
    });
  };

  // Function to handle Excel Printing from the Month End Billing page
   const printDataExcelMonthBilling = async (data = [], isData = false) => {
    let dataToPrint =
      selectedRows && selectedRows.length > 0 ? selectedRows : defaultRowsForExport(listData);
    if (isData) {
      dataToPrint = data;
    }
   
    if (!dataToPrint || dataToPrint.length === 0){

      return;
    } 

    const monthTxt = moment(new Date(parseInt(selectedYear, 10), selectedMonth - 1, 1)).format('MMMM')
    dataToPrint.forEach(v => {
      (v as any)['month'] = monthTxt
    })

    const sheetName = moment(new Date(parseInt(selectedYear, 10), selectedMonth - 1, 1)).format('MMM').toUpperCase()
    const blob = await writeExcel_ClientBilling(dataToPrint, sheetName)
    return blob
  }

  // Function to handle Excel Printing from the Ad Hoc Billing page
  const printDataExcelAdHoc = async () => {
    let dataToPrint =
      selectedRows && selectedRows.length > 0
        ? selectedRows
        : listData//.filter(item => item.needDiligentSearch !== "TRUE");
  
    if (!dataToPrint || dataToPrint.length === 0) return;

    const monthTxt = moment(
      new Date(parseInt(selectedYear, 10), selectedMonth - 1, 1)
    ).format("MMMM");
    dataToPrint.forEach(v => {
      (v as any).month = monthTxt;
    });
  
    const sheetName = `${selectedPeriod}- ${
      selectedPeriod === "Monthly"
        ? moment(
            new Date(parseInt(selectedYear, 10), selectedMonth - 1, 1)
          ).format("MMMM")
        : moment(new Date(parseInt(selectedYear, 10), 0, 1)).format("YYYY")
    }`;
    const blob = await writeExcel_ClientBilling(dataToPrint, sheetName);
    saveAs(blob, buildReportDocumentFilename({
      month: selectedMonth,
      year: selectedYear,
      clientName: selectedPartner.customerName || 'All Clients',
      reportType: 'CompletedFilings',
      extension: 'xlsx',
    }));
  };

    const printDataPdf = async (isMonthEndBilling: boolean = false) => {
    var dataToPrint = (selectedRows && selectedRows.length > 0 ? selectedRows : listData);

    // Month End Billing route only: server-side PDF via get-month-end-billing-pdf
    if (!isMonthEndBilling && window.location.href.split('/').includes('MonthEndBillingModule')) {
      try {
        setloading(true);

        Swal.fire({
          title: 'Generating PDF...',
          text: 'Please wait while we generate your PDF report.',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        const requestPayload = {
          month: selectedMonth,
          year: parseInt(selectedYear),
          partnerId: selectedPartner?.partnersId || 0,
          userTimezone: 'America/Denver',
          companyRollup: billingCompanyRollup,
        };

        const responseJson = await apiClient('/MonthEndBilling/get-month-end-billing-pdf', {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify(requestPayload),
        });

        setloading(false);
        Swal.close();

        if (!responseJson) {
          Swal.fire({
            title: 'Error!',
            text: 'Failed to get response from server.',
            icon: 'error',
            confirmButtonColor: '#02A4E3'
          });
          return;
        }

        if (responseJson.status && responseJson.data && responseJson.data.pdf_url) {
          const link = document.createElement('a');
          link.href = responseJson.data.pdf_url;
          link.download = responseJson.data.filename || 'report.pdf';
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          Swal.fire({
            title: 'Success!',
            text: `PDF downloaded successfully!`,
            icon: 'success',
            confirmButtonColor: '#02A4E3',
            timer: 3000,
            timerProgressBar: true
          });
          return;
        }

        Swal.fire({
          title: 'Error!',
          text: 'Invalid response format from server. Please contact support.',
          icon: 'error',
          confirmButtonColor: '#02A4E3'
        });
        return;
      } catch (error) {
        setloading(false);
        Swal.close();

        Swal.fire({
          title: 'Error!',
          text: 'An error occurred while generating the PDF. Please try again.',
          icon: 'error',
          confirmButtonColor: '#02A4E3'
        });
        return;
      }
    }

    if (window.location.href.split('/').includes('MonthEndBillingModule')) {
      return;
    }

    // Client-side PDF (Client Billing Report and other routes)
    setloading(false)

    dataToPrint = [...dataToPrint].sort((a: any, b: any) => {
      if (a.state && b.state) {
        const stateComparison = a.state.localeCompare(b.state);
        if (stateComparison !== 0) return stateComparison;
      }
      if (a.insuredName && b.insuredName) {
        return a.insuredName.localeCompare(b.insuredName);
      }
      return 0;
    });
    // const dataToPrint = selectedRows && selectedRows.length > 0 ? selectedRows : listData;
    const groupedByClient = dataToPrint.reduce((acc: Record<string, ClientBillingReportModel[]>, row: any) => {
      const client = row.partnerName;
      if (client) {
        if (!acc[client]) {
          acc[client] = [];
        }
        acc[client].push(row);
      }
      return acc;
    }, {} as Record<string, ClientBillingReportModel[]>);

      // To Handle zero filings — still need to show something
    if (Object.keys(groupedByClient).length === 0) {
      groupedByClient[selectedPartner.customerName || 'All Clients'] = []; // Ensure it runs through logic at least once
   }

    for (const [client, rows] of Object.entries(groupedByClient) as [string, ClientBillingReportModel[]][]) {
      const groupedData = rows.reduce((acc: Record<string, ClientBillingReportModel[]>, row:any) => {
        const state = row.state;
        if (state) {
          if (!acc[state]) {
            acc[state] = [];
          }
          acc[state].push(row);
        }
        return acc;
      }, {} as Record<string, ClientBillingReportModel[]>);

      printPDFReport(client, selectedYear, selectedMonth, groupedData, Columns)
    }
  };
  type ClientBillingReportModels = {
    [key: string]: any; // Adjust this type according to your data model
  };

  const generatePDFWithHtml2Pdf = async (fileName: string, htmlString: string): Promise<Blob> => {

    // 1) Create a container and inject your HTML
    const element = document.createElement('div');
    element.innerHTML = htmlString;

    // 2) WAIT FOR ALL FONTS TO LOAD (so Calibri Light metrics are ready)
    await (document as any).fonts.ready;

    // const scale = htmlString.length > 1500000 ? 0.7 : htmlString.length > 1000000 ? 0.9 : htmlString.length > 500000 ? 1.3 : 2
    // 3) Now set up html2pdf options with a fixed scale
    const options = {
      margin: 10,
      filename: `${fileName}.pdf`,
      pagebreak: { mode: ["css"] },
      // image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 1.5,
        useCORS: true,
        logging: false,
        dpi: 72,
        letterRendering: false,
        allowTaint: true,
        scrollX: 0,
        scrollY: 0,
      },
      jsPDF: { unit: 'mm', format: 'letter', orientation: 'landscape', hotfixes: ['px_scaling'] },
    };

    // 4) Generate the PDF immediately (no setTimeout)
    const pdfBlob = await html2pdf().from(element).set(options).outputPdf("blob")
    return pdfBlob
  }
    
  //Function to generate and download 5 PDF when the user clicks the "Month End Billing" button.
  const generateAndDownloadPDF = async (data: ClientBillingReportModels[], fileName: BillingReportNames, columnNames: { headerName: string; field: string; hide: boolean }[]) => {
    let localData = [...data]; 
    

    


    localData = [...localData].sort((a: any, b: any) => {
      const aState = a.stateName || a.state;
      const bState = b.stateName || b.state;
      if (aState && bState) {
        const stateComparison = aState.localeCompare(bState);
        if (stateComparison !== 0) return stateComparison;
      }
      if (a.insuredName && b.insuredName) {
        return a.insuredName.localeCompare(b.insuredName);
      }
      return 0;
    });

    if (fileName === BillingReportNames.UpcomingStateReport || fileName === BillingReportNames.CompletedStateReport) {
      localData = [...localData].sort((a: any, b: any) => {
        const columns = ['stateName', 'state', 'name', 'reportName', 'licensee'];

        for (let column of columns) {
          if (a[column] && b[column]) {
            const comparison = a[column].toString().localeCompare(b[column].toString());
            if (comparison !== 0) return comparison;
          }
        }
        return 0;
      });
    }
    let client;

    const groupedByClient = localData.reduce<Record<string, ClientBillingReportModels[]>>((acc, row) => {
      client = row.partnerName;
      if (client) {
        if (!acc[client]) {
          acc[client] = [];
        }
        acc[client].push(row);
      }
      return acc;
    }, {} as Record<string, ClientBillingReportModel[]>);

    // Define the type of grouped data
    const groupedData: Record<string, ClientBillingReportModels[]> = localData.reduce((acc, row) => {
      const state = row.stateName || row.state; // Adjust the field name as per your data
      if (state) {
        if (!acc[state]) {
          acc[state] = [];
        }
        acc[state].push(row);
      }
      return acc;
    }, {});

    var html = ''
    let grandTotalFeeDue = 0;

    if (Object.keys(groupedData).length === 0) {
      groupedData[''] = []
    }



    // const clientName = client;
    const clientName = `${selectedPartner.customerName || 'All Clients'}`

    // Use MISC logo for consistent branding
    const logoSvg = "/assets/brand/MISC_Brandmark_Secondary(1).png";

    html = `
          <html>
              <head>
                  <title>${fileName}</title>
                  <style>
                    * { 
                      box-sizing: border-box; 
                      margin: 0; 
                      padding: 0; 
                    }
                    body { 
                      font-family: Arial, sans-serif; 
                      font-size: 10px; 
                      margin: 0; 
                      padding: 15px; 
                      line-height: 1.3;
                      background: white;
                    }
                    .header { 
                      display: flex; 
                      align-items: center; 
                      margin-bottom: 20px; 
                      border-bottom: 2px solid #2099D2; 
                      padding-bottom: 10px;
                    }
                    .header img { 
                      height: 50px; 
                      width: 58px; 
                      margin-right: 15px; 
                      object-fit: contain;
                    }
                    .header h1 { 
                      margin: 0; 
                      font-size: 18px; 
                      color: #2099D2; 
                      font-weight: bold;
                    }
                    .meta { 
                      text-align: right; 
                      margin-bottom: 15px; 
                      font-size: 10px; 
                      color: #666;
                    }
                    .client-section { 
                      margin-bottom: 20px; 
                    }
                    .client-header { 
                      background: #f0f0f0 !important; 
                      padding: 8px; 
                      font-weight: bold; 
                      border-left: 4px solid #2099D2;
                      font-size: 12px;
                      margin-bottom: 5px;
                      -webkit-print-color-adjust: exact !important;
                      color-adjust: exact !important;
                    }
                    .date-range { 
                      font-size: 10px; 
                      color: #666; 
                      margin: 5px 0 15px 0;
                    }
                    .table-section {
                      margin-bottom: 20px;
                    }
                    table { 
                      width: 100%; 
                      border-collapse: collapse; 
                      margin-bottom: 15px; 
                      font-size: 9px;
                      table-layout: fixed;
                      border: 1px solid #ddd;
                    }
                    th { 
                      background: #2099D2 !important; 
                      color: white !important; 
                      padding: 6px 3px; 
                      text-align: center; 
                      font-weight: bold;
                      border: 1px solid #ddd;
                      font-size: 8px;
                      vertical-align: middle;
                      -webkit-print-color-adjust: exact !important;
                      color-adjust: exact !important;
                      word-wrap: break-word;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    }
                    td { 
                      padding: 3px 2px; 
                      text-align: center; 
                      border: 1px solid #ddd; 
                      word-wrap: break-word;
                      font-size: 8px;
                      vertical-align: middle;
                      overflow: hidden;
                      text-overflow: ellipsis;
                      max-width: 0;
                    }
                    tr:nth-child(even) { 
                      background: #f9f9f9 !important; 
                      -webkit-print-color-adjust: exact !important;
                      color-adjust: exact !important;
                    }
                    .total-row { 
                      font-weight: bold; 
                      background: #e6f3ff !important; 
                      -webkit-print-color-adjust: exact !important;
                      color-adjust: exact !important;
                    }
                    .currency { 
                      text-align: right; 
                    }
                    .feedue-section {
                      margin: 20px 0;
                    }
                    .footer-section {
                      margin-top: 30px;
                      padding: 15px;
                      background: #f9f9f9;
                      border-radius: 5px;
                    }
                    .footer-label {
                      margin-bottom: 20px;
                      font-weight: bold;
                    }
                    .footer-timestamp {
                      text-align: left;
                      font-size: 14px;
                      color: #555;
                      margin-top: 20px;
                    }
                    @media print {
                      * { 
                        -webkit-print-color-adjust: exact !important; 
                        color-adjust: exact !important; 
                      }
                      body { 
                        font-size: 8px;
                        margin: 10px;
                        padding: 10px;
                      }
                      .header {
                        margin-bottom: 15px;
                      }
                      .header h1 { 
                        font-size: 16px; 
                      }
                      .header img {
                        height: 45px;
                        width: 52px;
                      }
                      table { 
                        font-size: 7px; 
                        table-layout: fixed;
                        margin-bottom: 10px;
                      }
                      th { 
                        background: #2099D2 !important; 
                        color: white !important; 
                        padding: 4px 2px;
                        font-size: 7px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                      }
                      td {
                        padding: 2px 1px;
                        font-size: 7px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                      }
                      tr:nth-child(even) { 
                        background: #f9f9f9 !important; 
                      }
                      .total-row {
                        background: #e6f3ff !important;
                      }
                      .client-header {
                        background: #f0f0f0 !important;
                        font-size: 10px;
                      }
                      .date-range {
                        font-size: 8px;
                      }
                      .meta {
                        font-size: 8px;
                      }
                    }
                  </style>
              </head>
              <body>
                <div class="header">
                  <img src="${logoSvg}" alt="MISC Logo">
                  <h1>${fileName}</h1>
                </div>
                <div class="meta">
                  Generated: ${formatUtcToLocal(new Date().toISOString(), "dddd, MMMM DD, YYYY")} at ${formatUtcToLocal(new Date().toISOString(), "hh:mm A")}
                </div>
      `

      Object.entries(groupedByClient).map(([key, val], index) => {

        const groupedByState = val.reduce<Record<string, ClientBillingReportModels[]>>((acc, row) => {
          const state = row.stateName || row.state
          if (state) {
            if (!acc[state]) {
              acc[state] = []
            }
            acc[state].push(row)
          }
          return acc
        }, {})

        let info_Client = ``
        if ([BillingReportNames.UpcomingStateReport, BillingReportNames.CompletedStateReport].includes(fileName)) {
          info_Client = `
            <div class="client-section">
              <div class="client-header">${key || 'All Clients'}</div>
            </div>
          `
        }
        let info_StartEndDate = ''
        if ([BillingReportNames.UpcomingStateReport, BillingReportNames.CompletedStateReport].includes(fileName)) {
          let startDate = moment(new Date(parseInt(selectedYear, 10), selectedMonth - 1, 1))
          let endDate = moment(new Date(parseInt(selectedYear, 10), selectedMonth, 0))
          if ([BillingReportNames.UpcomingStateReport].includes(fileName)) {
            startDate = startDate.add(1, 'M')
            endDate = endDate.add(1, 'M')
          }
          info_StartEndDate = `
            <div class="date-range">
              Start Date: ${startDate.format("MM/DD/YYYY")} | End Date: ${endDate.format("MM/DD/YYYY")}
            </div>
          `
        }
        const titleSection = `
          <div>
            ${info_Client}
            ${info_StartEndDate}
          </div>
        `

        // Draw table section
        let tableSection = `<div class='table-section'>`;
        for (const [state, rows] of Object.entries(groupedByState)) {
          tableSection += `<table><thead><tr>`;

          columnNames.forEach(col => {
            if (!col.hide) {
              if (fileName === BillingReportNames.UpcomingStateReport) {
                if (col.field == "name" || col.field == "reportName") {
                  tableSection += `<th style="width: 12% ">${col.headerName}</th>`; // reduced to 12 from 14
                } else if (col.field == "stateName" || col.field == "state") {
                  tableSection += `<th style="width: 4%">${col.headerName}</th>`; // increase to 4 from 3
                } else if (col.field == "dueDate") {
                  tableSection += `<th style="width: 6%">${col.headerName}</th>`; // reduced to 6 from 7
                } else if (col.field == "period") {
                  tableSection += `<th style="width: 6%">${col.headerName}</th>`; // reduced 6 from 8
                } else if (col.field == "paymentType") {
                  tableSection += `<th style="width: 6%">${col.headerName}</th>`; // reduced 6 from 8
                } else if (col.field == "licensee") {
                  tableSection += `<th style="width: 10%">${col.headerName}</th>`; // reduced to 10 from 15
                } else if (col.field == "payment") {
                  tableSection += `<th style="width: 5%">${col.headerName}</th>`; // reduced to 5 from 6
                } else if (col.field == "reportRequired") {
                  tableSection += `<th style="width: 5%">${col.headerName}</th>`; // reduced to 5 from 6
                } else if (col.field == "wetSignature") {
                  tableSection += `<th style="width: 6%">${col.headerName}</th>`; // reduced to 5 from 5
                } else if (col.field == "notaryStamp") {
                  tableSection += `<th style="width: 6%">${col.headerName}</th>`; // reduced to 5 from 6
                } else {
                  tableSection += `<th style="width: 6%">${col.headerName}</th>`; // added width of 6%
                }
              }
              else if (fileName === BillingReportNames.OutstandingStateReport) {
                if (col.field == "partnerName") {
                  tableSection += `<th style="width: 15%">${col.headerName}</th>`;
                } else if (col.field == "state") {
                  tableSection += `<th style="width: 4%">${col.headerName}</th>`;
                } else if (col.field == "reportName") {
                  tableSection += `<th style="width: 15%">${col.headerName}</th>`;
                } else if (col.field == "dueDate") {
                  tableSection += `<th style="width: 6%">${col.headerName}</th>`;
                } else if (col.field == "licensee") {
                  tableSection += `<th style="width: 15%">${col.headerName}</th>`;
                } else if (col.field == "licenseNumber") {
                  tableSection += `<th style="width: 8%">${col.headerName}</th>`;
                } else if (col.field == "payment") {
                  tableSection += `<th style="width: 8%">${col.headerName}</th>`;
                } else if (col.field == "paymentType") {
                  tableSection += `<th style="width: 10%">${col.headerName}</th>`;
                } else if (col.field == "reportRequired") {
                  tableSection += `<th style="width: 8%">${col.headerName}</th>`;
                } else if (col.field == "mainLicensee") {
                  tableSection += `<th style="width: 6%">${col.headerName}</th>`;
                } else {
                  tableSection += `<th style="width: 7%">${col.headerName}</th>`; // added width of 7%
                }
              }
              else if (fileName === BillingReportNames.CompletedStateReport) {
                if (col.field == "partnerName") {
                  tableSection += `<th style="width: 14%">${col.headerName}</th>`;
                } else if (col.field == "name" || col.field == "reportName") {
                  tableSection += `<th style="width: 14%">${col.headerName}</th>`; //reduced to 14 from 16
                } else if (col.field == "stateName" || col.field == "state") {
                  tableSection += `<th style="width: 4%">${col.headerName}</th>`; // increased to 4 from 3
                } else if (col.field == "period") {
                  tableSection += `<th style="width: 6%">${col.headerName}</th>`; // reduced to 6 from 8
                } else if (col.field == "licensee") {
                  tableSection += `<th style="width: 12%">${col.headerName}</th>`; // reduced 10 from 12
                } else if (col.field == "payment") {
                  tableSection += `<th style="width: 5%">${col.headerName}</th>`; // remains the same
                } else if (col.field == "paymentType") {
                  tableSection += `<th style="width: 10%">${col.headerName}</th>`; // reduced to 8 from 10
                } else if (col.field == "report" || col.field == "reportRequired") {
                  tableSection += `<th style="width: 5%">${col.headerName}</th>`; // remains the same
                } else if (col.field == "mainLicensee") {
                  tableSection += `<th style="width: 5%">${col.headerName}</th>`; // remains the same
                } else if (col.field == "taxAmount") {
                  tableSection += `<th style="width: 9%">${col.headerName}</th>`; // reduced to 6 from 7
                } else if (col.field == "feeDue") {
                  tableSection += `<th style="width: 6%">${col.headerName}</th>`; // remains the same
                } else if (col.field == "dateFinalReportSentToClient") {
                  tableSection += `<th style="width: 10%">${col.headerName}</th>`; // reduced to 10 from 12
                } else {
                  tableSection += `<th style="width: 7%">${col.headerName}</th>`; // added width of 7%
                }
              }
 else {
                tableSection += `<th>${col.headerName}</th>`;
              }
            }
          });

          tableSection += '</tr></thead><tbody>';

          rows.forEach(row => {
            tableSection += '<tr>';
            columnNames.forEach(col => {
              if (!col.hide) {
                const value = (row as any)[col.field];
                let formattedValue = value ?? "";

                switch (col.field) {
                  case "payment":
                  case "report":
                  case "reportRequired":
                  case "wetSignature":
                  case "notaryStamp":
                  case "mainLicensee":
                    formattedValue = value
                      ? `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="black" viewBox="0 0 24 24">
                                          <rect width="20" height="20" x="2" y="2" fill="none" stroke="black" stroke-width="2" rx="2" ry="2"></rect>
                                          <path d="M7 13l3 3 7-7" stroke="black" stroke-width="2" fill="none"></path>
                                      </svg>`
                      : `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="black" viewBox="0 0 24 24">
                                          <rect width="20" height="20" x="2" y="2" fill="none" stroke="black" stroke-width="2" rx="2" ry="2"></rect>
                                      </svg>`;
                    break;
                  case "taxAmount":
                    if (!parseFloat(value)) {
                      formattedValue = 'No preminum reported'
                      break;
                    }
                    formattedValue = formatPrice(value);
                    break;
                  case "feeDue": {
                    const feeNum = parseFeeDueToNumber(value);
                    formattedValue = formatPrice(feeNum) || "$0.00";
                    tableSection += `<td class="fee-dues">${formattedValue}</td>`;
                    grandTotalFeeDue += feeNum;
                    return;
                  }
                  case "dateFinal":
                    formattedValue = formatBillingDateFiled(value);
                    break;
                  case "dateFinalReportSentToClient":
                  case "transactionEffDate":
                  case "dueDate":
                    formattedValue = value ? formatUtcToLocal(value, "MM/DD/YYYY") : "";
                    break;
                }
                tableSection += `<td>${formattedValue}</td>`;
              }
            });
            tableSection += '</tr>';
          });

          tableSection += '</tbody></table>';
        }
        tableSection += '</div>'
        html += titleSection + tableSection
      })

      // Draw "Fee Due" section
      let feeDueSection = ''
      if ([BillingReportNames.CompletedStateReport].includes(fileName)) {
        feeDueSection += `
          <div class="feedue-section">
            <table style="width: 80%; margin: auto; border-collapse: collapse;">
              <style>
                .fee-due-total {text-align: right;color: red;font-weight: bold;font-size: 118%; text-indent: 35px;}
                .fee-due-amount {text-align: right;color: red;font-weight: bold;width: 100px;font-size: 118%; white-space: nowrap;}
              </style>
                <tfoot>
                  <tr>
                    <td colspan="${columnNames.length - 1}" class="fee-due-total">Fees Due</td>
                    <td class="fee-due-amount">
                        ${"$" + grandTotalFeeDue.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                    </td>
                  </tr>
                </tfoot>
            </table>
          </div>
        `;
      }

      // Draw footer section - timestamp, "Follow Up Dates", "Notes To Client"
      let footerSection = ''
      const currentDate = new Date();
      const dateTime = {
        date: formatUtcToLocal(currentDate.toISOString(), 'dddd, MMMM D, YYYY'),
        time: formatUtcToLocal(currentDate.toISOString(), 'h:mm A').toLowerCase()
      };

      let info_FollowUpDates = ''
      if ([BillingReportNames.OutstandingStateReport].includes(fileName)) {
        info_FollowUpDates = `
          <div class="footer-label">Follow Up Dates: </div>
          <div class="footer-label">Notes To Client: </div>
        `
      }
      let info_TimeStamp = ''
      if ([BillingReportNames.OutstandingStateReport, BillingReportNames.CompletedStateReport].includes(fileName)) {
        info_TimeStamp = `
          <div class="footer-timestamp">
            <div>${dateTime.date}</div><br/>
            <div>${dateTime.time}</div>
          </div>
        `
      }
      footerSection += `
        <div class="footer-section">
            ${info_FollowUpDates}
            ${info_TimeStamp}
        </div>
      `

      html += feeDueSection + footerSection

      html += `
        </body>
      </html>
      `;


    const pdfBlob = await generatePDFWithHtml2Pdf(
      buildReportDocumentFilename({
        month: selectedMonth,
        year: selectedYear,
        clientName: selectedPartner.customerName || 'All Clients',
        reportType: fileName === BillingReportNames.UpcomingStateReport
          ? 'upcoming'
          : fileName === BillingReportNames.OutstandingStateReport
            ? 'outstanding'
            : 'completed',
      }).replace(/\.pdf$/i, ''),
      html
    )

    return pdfBlob;
  };

  const columnConfig = {
    upcomingDueDates: [
      { headerName: 'State', field: 'stateName', hide: false },
      { headerName: 'Report Name', field: 'name', hide: false },
      { headerName: 'Period', field: 'period', hide: false },
      { headerName: 'Due Date', field: 'dueDate', hide: false },
      { headerName: 'Payment Type', field: 'paymentType', hide: false },
      { headerName: 'Licensee', field: 'licensee', hide: false },
      { headerName: 'Payment Required', field: 'payment', hide: false },
      { headerName: 'Report Required', field: 'report', hide: false },
      { headerName: 'Wet Signature Required', field: 'wetSignature', hide: false },
      { headerName: 'Notary Stamp Required', field: 'notaryStamp', hide: false },
    ],
    outstandingDueDates: [
      { headerName: 'State', field: 'stateName', hide: false },
      // { headerName: 'Client Name', field: 'partnerName', hide: false },
      { headerName: 'Report Name', field: 'name', hide: false },
      { headerName: 'Due Date', field: 'dueDate', hide: false },
      { headerName: 'Licensee', field: 'licensee', hide: false },
      { headerName: 'Licensee Number', field: 'licenseNumber', hide: false },
      { headerName: 'Payment Required', field: 'payment', hide: false },
      { headerName: 'Payment Type', field: 'paymentType', hide: false },
      { headerName: 'Report Required', field: 'report', hide: false },
      { headerName: 'Main Licensee', field: 'mainLicensee', hide: false },
    ],
    completedDueDates: [
      { headerName: 'State', field: 'stateName', hide: false },
      // { headerName: 'Client Name', field: 'partnerName', hide: false },
      { headerName: 'Report Name', field: 'name', hide: false },
      { headerName: 'Period', field: 'period', hide: false },
      { headerName: 'Licensee', field: 'licensee', hide: false },
      { headerName: 'Date Final Report Sent to Client', field: 'dateFinalReportSentToClient', hide: false },
      { headerName: 'Payment Required', field: 'payment', hide: false },
      { headerName: 'Payment Type', field: 'paymentType', hide: false },
      { headerName: 'Report Required', field: 'report', hide: false },
      { headerName: 'Main Licensee', field: 'mainLicensee', hide: false },
      { headerName: 'Premium Reported', field: 'taxAmount', hide: false },
      { headerName: 'Fee Due', field: 'feeDue', hide: false },
    ]
  };

  const [selectedOption, setSelectedOption] = useState<string>('0');
  const handleChange = (event: SelectChangeEvent<string>) => {

    setSelectedOption(event.target.value);
    printData(event.target.value, window.location.href.includes('MonthEndBillingModule'));

  };

  const printData = async (option: string, isMonthEndBilling: boolean = false) => {
    if (option === 'excel') {
      // if (window.location.href.includes('MonthEndBillingModule')) {
      //   const excelBlob = await printDataExcelMonthBilling();
      //   if (excelBlob) {
      //     saveAs(excelBlob, 'MonthEndBillingReport.xlsx');
      //   }
      // } else {
      //   await printDataExcelAdHoc();
      // }
      await printDataExcel();
    } else if (option === 'pdf') {
        printDataPdf(!isMonthEndBilling);
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'No option selected',
        text: 'Please select an option to proceed.',
      });
    }
    setSelectedOption('0');
  };

  const printDataExcel = async () => {
    // Fix issue #2: Remove pagination limit to get all records
    let dataToPrint =
      selectedRows && selectedRows.length > 0 ? selectedRows : defaultRowsForExport(listData);
    let grandTotalFeeDue = 0;
    try {
      const workbook = new ExcelJS.Workbook();
      const response = await fetch('/assets/brand/StateReportBillingTemplate.xlsx');
      const arrayBuffer = await response.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);
      const worksheet = workbook.getWorksheet(1);
      if (!worksheet) return alert("Something went wrong");

      // Clear the template content and start fresh
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber <= 10) { // Clear first 10 rows to remove logo and template content
          row.eachCell((cell, colNumber) => {
            cell.value = null;
            cell.fill = { type: 'pattern', pattern: 'none' };
            cell.font = {};
            cell.border = {};
          });
        }
      });

      let rowIndex = 1; // Start from row 1 since we cleared the template
      let filteredColumns = Columns.filter(col => col.headerName && col.headerName !== 'No' && col.headerName !== 'startDate' && col.headerName !== 'endDate')

      // Fix issue #1: Set correct title based on selected tab
      const getReportTitle = () => {
        return 'Month End Billing Report';
      };

      // Fix issue #3: Remove state grouping, show transactions in filter order
      const stateColumnIndex = filteredColumns.findIndex(col => col.headerName === 'state');
      if (stateColumnIndex !== -1) filteredColumns.unshift(filteredColumns.splice(stateColumnIndex, 1)[0]);
      const columnHeaders = filteredColumns.map(col => col.headerName);

      const addHeaderRow = (rowIdx: number) => {
        const headerRow = worksheet.getRow(rowIdx);
        columnHeaders.forEach((header, colIndex) => {
          if (header !== "Edit") {
            const cell = headerRow.getCell(colIndex + 1);
            cell.value = header;
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '808080' } }; // Header row in solid grey}
          }
        });
        return rowIdx + 1;
      };

      // Match PDF/API: use selected client name from the billing dropdown, not row partnerName (can differ e.g. parent agency).
      const getCompanyName = () => {
        if (selectedPartner.partnersId === 0 || !selectedPartner) {
          return 'All';
        }
        const fromSelection = (selectedPartner.customerName || '').trim();
        if (fromSelection) return fromSelection;
        return dataToPrint[0]?.partnerName || 'All';
      };

      // Set the report title (no "Filed Transactions" - just the correct title)
      worksheet.getRow(rowIndex).getCell(2).value = getReportTitle();
      worksheet.getRow(rowIndex).getCell(2).font = { bold: true, size: 16 };
      rowIndex++;

      // Set the company name
      worksheet.getRow(rowIndex).getCell(2).value = `Company: ${getCompanyName()}`;
      worksheet.getRow(rowIndex).getCell(2).font = { bold: true, size: 14 };
      rowIndex++;

      // Add billing period dates like report header (Start Date / End Date)
      const { startDate, endDate } = getBillingPeriodDisplayDates(parseInt(selectedYear, 10), selectedMonth);

      worksheet.getRow(rowIndex).getCell(2).value = `Start Date: ${startDate}`;
      worksheet.getRow(rowIndex).getCell(2).font = { bold: false, size: 12 };
      rowIndex++;

      worksheet.getRow(rowIndex).getCell(2).value = `End Date: ${endDate}`;
      worksheet.getRow(rowIndex).getCell(2).font = { bold: false, size: 12 };
      rowIndex += 2;

      // Add header row
      rowIndex = addHeaderRow(rowIndex);

      // Process all data in order (no grouping by client or state)
      let colorCycleIndex = 0;
      dataToPrint.forEach((row) => {
        const newFilteredColumns = filteredColumns.filter((val) => val.field != "stateRule")
        newFilteredColumns.forEach((col, colIndex) => {
          if (col.field == "largeReport" || col.field == "largeReportFee") {
            delete col.hide;
          }
          if (!col.hide) {
            let value = (row as any)[col.field];
            if (col.field === 'dateFinal') value = formatBillingDateFiled(value);

            if (col.field === 'feeDue') {
              const fee = parseFeeDueToNumber(value);
              grandTotalFeeDue += fee;
            }
            
            const cell = worksheet.getRow(rowIndex).getCell(colIndex + 1);

            cell.value = (col.field === "feeDue") ? parseFeeDueToNumber(value) : value;
            if (col.field === "feeDue") cell.numFmt = '"$"#,##0.00';
            // Apply alternating fill colors: white, light gray, dark gray
            const fillColors = ['FFFFFF', 'ededed', 'd9d9d9'];
            const fillColor = fillColors[colorCycleIndex % 3];
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
          }
        });

        rowIndex++;
        colorCycleIndex++; // Increment to apply the next color in the cycle
      });

      worksheet.getCell(`G${rowIndex}`).value = 'Total Fee Due';
      worksheet.getCell(`G${rowIndex}`).font = { bold: true, size: 15, color: { argb: "FF0000" } };
      worksheet.getCell(`H${rowIndex}`).value = grandTotalFeeDue;
      worksheet.getCell(`H${rowIndex}`).numFmt = '"$"#,##0.00';
      worksheet.getCell(`H${rowIndex}`).font = { bold: true, size: 15, color: { argb: "FF0000" } };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, buildReportDocumentFilename({
        month: selectedMonth,
        year: selectedYear,
        clientName: selectedPartner.customerName || 'All Clients',
        reportType: 'CompletedFilings',
        extension: 'xlsx',
      }));
    } catch (error) {
      console.error("Error generating the Excel file:", error);
    }
  };

  // Export to CSV Logic
  const exportToCSV = () => {
    const csvRows: any = [];
    let dataToPrint =
      selectedRows && selectedRows.length > 0 ? selectedRows : defaultRowsForExport(listData);
    // dataToPrint = [...dataToPrint].sort((a: any, b: any) => {
    //   if (a.state && b.state) {
    //       const stateComparison = a.state.localeCompare(b.state);
    //       if (stateComparison !== 0) return stateComparison; 
    //   }
    //   if (a.insuredName && b.insuredName) {
    //       return a.insuredName.localeCompare(b.insuredName);
    //   }
    //   return 0;
    // });
    // // const dataToPrint = selectedRows && selectedRows.length > 0 ? selectedRows : listData;
    // const groupedByClient = dataToPrint.reduce((acc, row) => {
    //   const client = row.partnerName;
    //   if (client) {
    //     if (!acc[client]) {
    //       acc[client] = [];
    //     }
    //     acc[client].push(row);
    //   }
    //   return acc;
    // }, {} as Record<string, ClientBillingReportModel[]>);
    let filteredColumns = [
      { "field": "state", "header": "State" },
      { "field": "invoiceNumber", "header": "Invoice Number" },
      { "field": "insuredName", "header": "Insured Name" },
      { "field": "policyNumber", "header": "Policy Number" },
      { "field": "filed", "header": "Status" },
      { "field": "dateFinal", "header": "Date Filed" },
      { "field": "transactionTypeName", "header": "Transaction Type" },
      { "field": "feeDue", "header": "Fee Due" }
    ]

    const headers = filteredColumns.map((item) => item.header)
    csvRows.push(headers.join(","));
  
    for (const row of dataToPrint) {
      const values = filteredColumns.map(header => {
        let val = '';
        if (header.field === 'feeDue') {
          const amount = (row as Record<string, any>)[header.field];
          val = amount !== undefined && amount !== null ? `$ ${amount}` : "";
        } else if (header.field === 'dateFinal') {
          val = formatBillingDateFiled((row as Record<string, any>)[header.field]);
        } else {
          val = (row as Record<string, any>)[header.field] ?? "";
        }
        
        return `"${String(val).replace(/"/g, '""')}"`; // handle commas and quotes
      });
      csvRows.push(values.join(","));
    }
  
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  
    // Create download link
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `${window.location.href.split('/').includes('MonthEndBillingModule') ? 'MonthEndBilling' : 'ClientBilling'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  function EditToolbar() {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: any) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const [toolbarMenuAnchor, setToolbarMenuAnchor] = useState<null | HTMLElement>(null);
    const openToolbarMenu = Boolean(toolbarMenuAnchor);
    const handleToolbarMenuClose = () => setToolbarMenuAnchor(null);
    const handleToolbarAction = (action: 'filter' | 'columns') => {
      handleToolbarMenuClose();
      const api = clientBillingGridApiRef.current;
      if (!api) return;
      if (action === 'filter') api.showFilterPanel();
      else if (action === 'columns') (api as any).showPreferences('columns');
    };

    const handleMonthEndBillingClick = async () => {

    try {
      setloading(true);
      
      // Check if "All" customers is selected (partnerId = "0")
      const isAllCustomers = selectedPartner.partnersId.toString() === "0";
      
      const includeStateReportsInMEB = AppFlags.enableStateReports;

      // Show appropriate loading dialog based on selection
      Swal.fire({
        title: isAllCustomers ? 'Generating Master Month End Billing ZIP...' : 'Generating Month End Billing Report...',
        text: isAllCustomers 
          ? 'Please wait while we generate individual customer reports and package them into a master ZIP file. This may take several minutes.'
          : includeStateReportsInMEB
            ? 'Please wait while we generate your complete PDF report with all 3 state report sections.'
            : 'Please wait while we generate your Month End Billing package (filed transactions and related reports).',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

              // For "All" customers (partnerId = "0"), we'll use the same endpoint
      // but the backend will detect partnerId = "0" and generate reports for all customers

      // **INDIVIDUAL CUSTOMER REPORT GENERATION**
      // Original logic for specific customer selection
      
      // Full month-end payload (billable + diligent) for ZIP Diligent Search policy ids — not the Filed Transactions Excel.
      let dataToPrint = selectedRows && selectedRows.length > 0 ? selectedRows : listData;
      const customerPolicyIds = dataToPrint
        .map((item) => item.customerPolicyId)
        .filter((id) => id != null && id !== 0)
        .join(",");

      // Get current UI filter and sort state (these should be passed as props from MonthEndBillingModule)
      // For now, using empty values but in production this should come from the parent component
      const currentFilterField = ""; // Would be filterModel?.items?.[0]?.columnField || ""
      const currentFilterValue = "";  // Would be filterModel?.items?.[0]?.value || ""
      const currentFilterOperator = ""; // Would be filterModel?.items?.[0]?.operatorValue || ""
      const currentSortField = "";    // Would be sortModel?.[0]?.field || ""
      const currentSortDirection = ""; // Would be sortModel?.[0]?.sort || ""

        // Build the comprehensive request payload - EXACTLY matching individual API calls
        const requestPayload = {
          // Core context 
          partnerId: selectedPartner.partnersId.toString(), // "0" when All customers selected
          companyRollup: billingCompanyRollup,
          ...(isAllCustomers
            ? {}
            : {
                partnerDisplayName:
                  (selectedPartner.customerName || '').trim() || undefined,
              }),
          month: selectedMonth,
          year: selectedYear,
          userId: user?.userId,
          
          // CRITICAL: Total record count for reference (0 when All customers since we don't filter)
          totalRecords: isAllCustomers ? 0 : dataToPrint.length,

          // Month End Billing configuration (EXACT match to /MonthEndBilling/get-month-end-billing GET params)
          monthEndBillingConfig: {
            enabled: true,
            // Convert to same format as individual Month End Billing call
            pageSize: 1000,
            currentPage: 1,
            period: selectedPeriod,          // "Monthly" or "Annual"
            partnerId: selectedPartner.partnersId.toString(), // "0" when All customers
            month: selectedPeriod === "Monthly" ? selectedMonth : "0", // Current month for Monthly
            year: selectedYear, // Current year
            companyRollup: billingCompanyRollup,
            
            // Current UI filter state (same as combineFilters logic)
            filters: JSON.stringify([]), // Empty for now, would contain active filters
            fieldName: currentFilterField,
            value: currentFilterValue,
            operator: currentFilterOperator,
            field: currentSortField,
            sort: currentSortDirection,
            
            // CRITICAL: For All customers (partnerId="0"), don't pass customerPolicyIds
            // The backend will fetch all customers' data when partnerId="0"
            customerPolicyIds: isAllCustomers ? "" : customerPolicyIds
          },

          // State Reports configuration (suppressed when REACT_APP_ENABLE_STATE_REPORTS is false)
          stateReportsConfig: {
            // Upcoming Reports (selectedTab: 1) - Use CURRENT month
            upcomingReports: {
              enabled: includeStateReportsInMEB,
              pageSize: 1000,
              currentPage: 1,
              PartnerId: selectedPartner.partnersId.toString(), // "0" when All customers
              month: selectedMonth, // Current month
              year: selectedYear, // Current year
              field: currentSortField,
              sort: currentSortDirection,
              fieldName: currentFilterField,
              value: currentFilterValue,
              operator: currentFilterOperator,
              selectedTab: 1,
              companyRollup: billingCompanyRollup,
            },
            // Completed Reports (selectedTab: 2) - Use CURRENT month
            completedReports: {
              enabled: includeStateReportsInMEB,
              pageSize: 1000,
              currentPage: 1,
              PartnerId: selectedPartner.partnersId.toString(), // "0" when All customers
              month: selectedMonth, // Current month
              year: selectedYear, // Current year
              field: currentSortField,
              sort: currentSortDirection,
              fieldName: currentFilterField,
              value: currentFilterValue,
              operator: currentFilterOperator,
              selectedTab: 2,
              companyRollup: billingCompanyRollup,
            },
            // Outstanding State Reports (selectedTab: 3)
            outstandingStateReports: {
              enabled: includeStateReportsInMEB,
              pageSize: 1000,
              currentPage: 1,
              PartnerId: selectedPartner.partnersId.toString(), // "0" when All customers
              month: selectedMonth,
              year: selectedYear,
              field: currentSortField,
              sort: currentSortDirection,
              fieldName: currentFilterField,
              value: currentFilterValue,
              operator: currentFilterOperator,
              selectedTab: 3,
              companyRollup: billingCompanyRollup,
            },
          // Non-Premium Endorsements
            nonPremiumEndorsement: {
              enabled: true,
              pageSize: 1000,
              currentPage: 1,
              PartnerId: selectedPartner.partnersId.toString(), // "0" when All customers
              month: selectedMonth,
              year: selectedYear,
              field: currentSortField,
              sort: currentSortDirection,
              fieldName: currentFilterField,
              value: currentFilterValue,
              operator: currentFilterOperator,
              selectedTab: 4,
              companyRollup: billingCompanyRollup,
            }
          },

          // Report inclusion
          includeReports: {
            upcomingStateReports: includeStateReportsInMEB,
            completedStateReports: includeStateReportsInMEB,
            outstandingStateReports: includeStateReportsInMEB,
            monthEndBilling: true,
            nonPremiumEndorsement: true
          },
          userTimezone: 'America/Denver'
        };

        // Single API call to generate comprehensive PDF
        const response = await apiClient('/MonthEndBilling/generate-comprehensive-month-end-billing-pdf', {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify(requestPayload)
        });

        setloading(false);
        Swal.close();

        // Check if we have a response
        if (!response) {
          throw new Error('No response received from server');
        }

        // Check for error indicators based on actual response structure
        // Backend uses 'status' field and 'resp' field
        const hasError = response.status === false || response.resp !== 0 || response.error || (response.message && response.message.toLowerCase().includes('error'));
        
        if (hasError) {
          throw new Error(response?.message || response?.error || 'Failed to generate PDF');
        }

        // Handle the response based on actual backend structure
        if (response.data?.zip_url) {
          
          
          // Direct download of the ZIP file
          window.open(response.data.zip_url, "_blank");
          
        } else if (response.data?.pdfBase64) {

          // Convert base64 to blob and download
          const byteCharacters = atob(response.data.pdfBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          
          const fileName = `Month_End_Billing_Comprehensive_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.pdf`;
          saveAs(blob, fileName);
          
        } else if (response.data?.pdfUrl) {

          // Direct URL download
          window.open(response.data.pdfUrl, "_blank");
          
        } else if (response.pdfBase64) {

          // Maybe the PDF data is at the root level
          const byteCharacters = atob(response.pdfBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          
          const fileName = `Month_End_Billing_Comprehensive_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.pdf`;
          saveAs(blob, fileName);
          
        } else {

          // Show a message about the response structure
          Swal.fire({
            title: "Response Received",
            text: `Server responded but no download data found. Message: ${response?.message || 'No message'}`,
            icon: "warning",
            confirmButtonText: "OK",
          });
          return; // Exit early since we can't process the download
        }

        // Show success with summary based on actual response data
        let summaryText = isAllCustomers 
          ? (response?.message || "Master ZIP file with all customer reports has been generated successfully.")
          : (response?.message || "Comprehensive Month End Billing report has been generated successfully.");
        
        // if (response.data?.reports_count) {
        //   summaryText += isAllCustomers ? `\n\n📊 Master ZIP Summary:` : `\n\n📊 Generation Summary:`;
        //   summaryText += `\n• Total Reports Generated: ${response.data.reports_count}`;
        //   summaryText += `\n• ZIP Filename: ${response.data.zip_filename || 'N/A'}`;
          
        //   // For All customers, show customer-specific data if available
        //   if (isAllCustomers && response.data?.customers_count) {
        //     summaryText += `\n• Total Customers Processed: ${response.data.customers_count}`;
            
        //     if (response.data.customer_files?.length > 0) {
        //       summaryText += `\n• Customer Files: ${response.data.customer_files.length} individual ZIP files`;
        //     }
        //   }
          
        //   if (response.data.generated_reports?.length > 0) {
        //     summaryText += `\n• Reports Included: ${response.data.generated_reports.join(', ')}`;
        //   }
          
        //   if (response.data.reports_included?.length > 0) {
        //     summaryText += `\n• Report Types: ${response.data.reports_included.join(', ')}`;
        //   }
          
        //   if (response.data.total_sections) {
        //     summaryText += `\n• Total Sections: ${response.data.total_sections}`;
        //   }
        // }

        Swal.fire({
          title: isAllCustomers ? "Master ZIP Generated!" : "Success!",
          text: summaryText,
          icon: "success",
          confirmButtonText: "OK",
        });

      } catch (error) {

        setloading(false);
        Swal.close();
        
        Swal.fire({
          title: "Error!",
          text: (error as Error).message || "An error occurred while generating the comprehensive PDF. Please try again later.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    };
    const toolbarButtonSx = {
      textTransform: 'none' as const,
      fontWeight: 600,
      borderColor: '#e2e8f0',
      color: '#475569',
      px: 1,
      '&:hover': {
        borderColor: '#2099d2',
        backgroundColor: 'rgba(32, 153, 210, 0.04)',
        color: '#2099d2',
      },
    };
    return (
      <React.Fragment>
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          gap={1.5}
          sx={{ px: 2, borderBottom: '1px solid #e2e8f0', backgroundColor: '#fafbfc', minHeight: 56 }}
        >
          {isTabletOrDown ? (
            <>
              <IconButton
                aria-label="Table options"
                aria-controls={openToolbarMenu ? 'table-options-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={openToolbarMenu ? 'true' : undefined}
                onClick={(e) => setToolbarMenuAnchor(e.currentTarget)}
                sx={{ color: '#2099d2' }}
              >
                <MoreVertOutlinedIcon />
              </IconButton>
              <Menu
                id="table-options-menu"
                anchorEl={toolbarMenuAnchor}
                open={openToolbarMenu}
                onClose={handleToolbarMenuClose}
                MenuListProps={{ 'aria-labelledby': 'table-options-button' }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => { handleToolbarAction('filter'); }}>Filter</MenuItem>
                <MenuItem onClick={() => { handleToolbarAction('columns'); }}>Columns</MenuItem>
                <MenuItem onClick={() => { exportToCSV(); handleToolbarMenuClose(); }}>Download as CSV</MenuItem>
                {selectedPartner.partnersId !== 0 && (
                  <MenuItem onClick={() => { printData('pdf', window.location.href.includes('MonthEndBillingModule')); handleToolbarMenuClose(); }}>Print</MenuItem>
                )}
              </Menu>
            </>
          ) : (
            <GridToolbarContainer sx={{ border: 0 }}>
              <GridToolbarColumnsButton />
              <GridToolbarFilterButton />
              <GridToolbarDensitySelector />
              <Button
                startIcon={<DownloadIcon />}
                onClick={handleClick}
                variant="outlined"
                size="medium"
                sx={toolbarButtonSx}
              >
                Export
              </Button>
              <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem
                  onClick={() => {
                    exportToCSV();
                    handleClose();
                  }}
                >
                  Download as CSV
                </MenuItem>
                {selectedPartner.partnersId !== 0 && (
                  <MenuItem
                    onClick={() => {
                      printData('pdf', window.location.href.includes('MonthEndBillingModule'));
                      handleClose();
                    }}
                  >
                    Print
                  </MenuItem>
                )}
              </Menu>
            </GridToolbarContainer>
          )}
          <Box display="flex" alignItems="center" gap={1} sx={{ flexWrap: 'wrap' }}>
            {user?.userRoles.split(",").includes("Super Admin") && (
              <Button
                variant="contained"
                size="small"
                onClick={handleMonthEndBillingClick}
                sx={{ textTransform: "none", fontWeight: 600, backgroundColor: "#2099d2", "&:hover": { backgroundColor: "#176990" } }}
              >
                Month End Billing
              </Button>
            )}
            {user?.userRoles.split(",").includes("Super Admin") && (
              <Button
                variant="outlined"
                size="medium"
                onClick={handleDelete}
                sx={toolbarButtonSx}
              >
                Remove
              </Button>
            )}
            {user?.userRoles.split(",").includes("Super Admin") && (
              <FormControl size="medium" sx={{ minWidth: 120 }}>
                <Select
                  value={selectedOption}
                  onChange={handleChange}
                  displayEmpty
                  size="medium"
                  IconComponent={ExpandMoreIcon}
                  renderValue={(selected) => (selected === '0' ? 'Print' : selected)}
                  sx={{
                    ...toolbarButtonSx,
                    backgroundColor: '#fff',
                    // minHeight: 36,
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    '.MuiInputBase-root' : {borderRadius: '5px !important'},
                    '& .MuiSelect-select' : {padding: '10px',},
                    '.MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2099d2' },
                  }}
                >
                  <MenuItem value="excel">Excel</MenuItem>
                  {selectedPartner.partnersId !== 0 && <MenuItem value="pdf">PDF</MenuItem>}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>
      </React.Fragment>
    );
  }
  const Columns: GridColDef[] = [
    {
      field: 'rowNum',
      type: 'number',
      headerName: 'No',
      hide: true,
      hideable: false,
      disableExport: true,
      flex: 1
    },
    {
      field: "state",
      type: "text",
      headerName: "State",
      minWidth: 140,
      headerAlign: "left",
      align: "left",
      hideSortIcons: true,
      flex: 1,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'state'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )
    },
      valueGetter: (params) => params.value,
    },
    {
      field: "invoiceNumber",
      type: "text",
      headerName: "Invoice Number",
      minWidth: 175,
      headerAlign: "left",
      align: "left",
      flex: 1,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'invoiceNumber'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )
    },
      valueGetter: (params) => params.value,
    },
    {
      field: "insuredName",
      type: "text",
      headerName: "Insured Name",
      minWidth: 270,
      headerAlign: "left",
      align: "left",
      hideSortIcons: true,
      flex: 1,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'insuredName'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
        </div>
        )
      },
    },
    {
      field: "policyNumber",
      type: "text",
      headerName: "Policy Number",
      minWidth: 190,
      headerAlign: "left",
      align: "left",
      hideSortIcons: true,
      flex: 1,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'policyNumber'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
          <div>
            <span className="table-card-title">{params.colDef.headerName}</span>
            {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
          </div>
        )
      },
      valueGetter: (params) => params.value,
    },
    {
      field: "filed",
      type: "text",
      headerName: "Status",
      minWidth: 120,
      headerAlign: "left",
      align: "left",
      flex: 1,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'filed'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
          <div>
            <span className="table-card-title">{params.colDef.headerName}</span>
            {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
          </div>
        )
      },
    },
    {
      field: "dateFinal",
      type: "text",
      headerName: "Date Filed",
      minWidth: 140,
      headerAlign: "left",
      align: "left",
      flex: 1,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'dateFinal'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
        </div>
      )
      },
      valueGetter: (params) => formatBillingDateFiled(params.value),
    },
    {
      field: "transactionTypeName",
      type: "text",
      headerName: "Transaction Type",
      minWidth: 200,
      headerAlign: "left",
      align: "left",
      flex: 1,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'transactionTypeName'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
        </div>
      )
      },
      valueGetter: (params) => params.value,
    },
    {
      field: "feeDue",
      type: "text",
      headerName: "Fee Due",
      minWidth: 200,
      headerAlign: "right",
      align: "right",
      flex: 1,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'feeDue'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
        </div>
      )
      },
      valueFormatter: (params) => formatPrice(params.value) || "",
      renderEditCell: (params) => <FeeDueGridEditInputCell {...params} />,
      editable: true, // Make the feeDue column editable
    },
  ];

  const ColumnsForDiligents: GridColDef[] = [
    {
      field: 'rowNum',
      type: 'number',
      headerName: 'No',
      hide: true,
      hideable: false,
      disableExport: true,
    },

    {
      field: "state",
      type: "text",
      headerName: "State",
      minWidth: 140,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value,
    },
    {
      field: "invoiceNumber",
      type: "text",
      headerName: "Invoice Number",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value,
    },
    {
      field: "insuredName",
      type: "text",
      headerName: "Insured Name",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
    },
    {
      field: "policyNumber",
      type: "text",
      headerName: "Policy Number",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value,
    },
    {
      field: "needDiligentSearch",
      type: "text",
      headerName: "Need Diligent Search",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value,
    },
    {
      field: "policyEffDate",
      type: "text",
      headerName: "PolicyEffDate",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => {
        const dateValue = params.value;

        if (dateValue) {
          const date = new Date(dateValue);
          const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
          const day = String(date.getDate()).padStart(2, '0');
          const year = date.getFullYear();

          return `${month}/${day}/${year}`;
        }

        return "";
      },
      // valueGetter: (params) => params.value,
    },
    {
      field: "policyExpDate",
      type: "text",
      headerName: "PolicyExpDate",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => {
        const dateValue = params.value;

        if (dateValue) {
          const date = new Date(dateValue);
          const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
          const day = String(date.getDate()).padStart(2, '0');
          const year = date.getFullYear();

          return `${month}/${day}/${year}`;
        }

        return "";
      },
      // valueGetter: (params) => params.value,
    },
    {
      field: "transactionType",
      type: "text",
      headerName: "Transaction Type",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value,
    },
    {
      field: "grossPremium",
      type: "text",
      headerName: "Gross Premium",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value,
      editable: true,
    },
    {
      field: "feeDue",
      type: "text",
      headerName: "Fee Due",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueFormatter: (params) => formatPrice(params.value) || "",
      renderEditCell: (params) => <FeeDueGridEditInputCell {...params} />,
      editable: true, // Make the feeDue column editable
    },
  ];



  const handleFeeDueCellEditCommit = (params: GridCellEditCommitParams) => {
    if (params.field !== "feeDue") return

    const exsistinfRec = listData.find(
      (x) => String(x.rowNum) === String(params.id) || x.rowNum === params.id
    )
    if (!exsistinfRec) return

    const prevFee = exsistinfRec.feeDue
    const updatedRow: ClientBillingReportModel = {
      ...exsistinfRec,
      feeDue: params.value as ClientBillingReportModel["feeDue"],
    }

    setItemList((rows) =>
      rows.map((row) =>
        row.rowNum === exsistinfRec.rowNum ? { ...row, feeDue: params.value } : row
      )
    )

    const isMeb =
      typeof window !== "undefined" && window.location.href.includes("MonthEndBillingModule")
    const canPersist =
      isMeb && userCanSaveMonthEndBillingFeeOverride(user?.userRoles) && Boolean(token)

    if (!canPersist) {
      return
    }

    const year = parseInt(String(selectedYear), 10)
    const month = Number(selectedMonth)
    if (!Number.isFinite(year) || year < 2000 || year > 2100) {
      setItemList((prev) =>
        prev.map((r) =>
          r.rowNum === exsistinfRec.rowNum ? { ...r, feeDue: prevFee } : r
        )
      )
      Swal.fire({
        icon: "error",
        title: "Invalid billing year",
        text: "Select a valid year before saving fee overrides.",
        confirmButtonText: "OK",
      })
      return
    }
    if (!Number.isInteger(month) || month < 1 || month > 12) {
      setItemList((prev) =>
        prev.map((r) =>
          r.rowNum === exsistinfRec.rowNum ? { ...r, feeDue: prevFee } : r
        )
      )
      Swal.fire({
        icon: "error",
        title: "Invalid billing month",
        text: "Select a valid month before saving fee overrides.",
        confirmButtonText: "OK",
      })
      return
    }

    const feeNorm = normalizeFeeDueOverrideForApi(String(updatedRow.feeDue ?? ""))
    const prevNorm = normalizeFeeDueOverrideForApi(String(prevFee ?? ""))
    if (prevNorm === feeNorm) {
      return
    }

    const lineSignature = buildMonthEndBillingLineSignature({
      ...exsistinfRec,
      ...updatedRow,
    })

    void (async () => {
      try {
        setFeeOverrideSaving(true)
        const resp = await apiClient("/MonthEndBilling/save-month-end-billing-fee-override", {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify({
            CustomerPolicyId: updatedRow.customerPolicyId,
            BillingYear: year,
            BillingMonth: month,
            LineSignature: lineSignature,
            FeeDueOverride: feeNorm,
          }),
        })
        const failed =
          !resp ||
          resp.ok === false ||
          resp.status === false ||
          (typeof resp.status === "number" && resp.status >= 400)
        if (failed) {
          setItemList((prev) =>
            prev.map((r) =>
              r.rowNum === updatedRow.rowNum ? { ...r, feeDue: prevFee } : r
            )
          )
          Swal.fire({
            icon: "error",
            title: "Could not save fee",
            text: resp?.message || "Request failed",
            confirmButtonText: "OK",
          })
        }
      } catch (e: any) {
        setItemList((prev) =>
          prev.map((r) =>
            r.rowNum === updatedRow.rowNum ? { ...r, feeDue: prevFee } : r
          )
        )
        Swal.fire({
          icon: "error",
          title: "Could not save fee",
          text: e?.message || "Error",
          confirmButtonText: "OK",
        })
      } finally {
        setFeeOverrideSaving(false)
      }
    })()
  }


  return (
    <>
      <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
        {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
      </div>
      <div 
        style={{
            height: "calc(100vh - 300px)",
            display: "flex",
            flexDirection: "column",
            width: "100%",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            position: "relative",
        }}
      >
        {(dataLoading || feeOverrideSaving) && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              backgroundColor: "rgba(250, 251, 252, 0.85)",
              zIndex: 10,
              border: "1px solid #e2e8f0",
              borderRadius: 1,
            }}
          >
            <Box sx={{ width: "100%", maxWidth: 320 }}>
              <LinearProgress color="info" sx={{ backgroundColor: "#e2e8f0", "& .MuiLinearProgress-bar": { backgroundColor: "#132D3D" } }} />
              <Box sx={{ mt: 1.5, textAlign: "center" }}>
                <span style={{ fontSize: "14px", fontWeight: 600, color: "#475569" }}>Loading...</span>
              </Box>
            </Box>
          </Box>
        )}
        <div style={{ flex: 1, overflow: "auto" }}>
          <DataGridPro
            apiRef={clientBillingGridApiRef}
            //rows={listData.filter(item => item.needDiligentSearch === "")}
            //rows={listData}
            // New logic
            //getRowId={(r) => r.rowNum}
            getRowId={(row) => row.rowNum}
            rows={mebGridRows(listData)}
            columns={Columns}
            loading={dataLoading || feeOverrideSaving}
            pageSize={_pageSize}
            pagination
            rowsPerPageOptions={[10, 25, 50]}
            editMode="cell"
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            onCellEditCommit={handleFeeDueCellEditCommit}
            checkboxSelection={
              user?.userRoles.split(",").includes("Super Admin")
                ? true
                : false
            }
            filterMode="server"
            onFilterModelChange={handleFilterChange}
            sortingMode="server"
            sortingOrder={['asc', 'desc']}
            onSortModelChange={handleSortChange}
            isCellEditable={(params) =>
              params.colDef.field === "feeDue" &&
              typeof window !== "undefined" &&
              window.location.href.includes("MonthEndBillingModule") &&
              userCanSaveMonthEndBillingFeeOverride(user?.userRoles)
            }
            onSelectionModelChange={
              user?.userRoles.split(",").includes("Super Admin")
                ? (newRowSelectionModel) => {
                  onRowsSelectionHandler(newRowSelectionModel)
                }
                : undefined
            }
            components={{
              Toolbar: EditToolbar,
            }}
            sx={{
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              border: 'none',
              fontSize: '14px',
              color: '#475569',
              backgroundColor: '#ffffff',
              "& .MuiDataGridPro-columnHeaderTitle": {
                  whiteSpace: 'normal',
                  lineHeight: 1.35,
                  fontWeight: 600,
                  fontSize: '12px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: '#64748b',
              },
              "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: '#f8fafc',
                  borderBottom: '2px solid #e2e8f0',
                  // borderTop: '2px solid #e2e8f0',
                  minHeight: '52px !important',
                  maxHeight: '52px !important',
              },
              "& .MuiDataGrid-columnHeader": {
                  "&:focus, &:focus-within": { outline: 'none' },
              },
              "& .MuiDataGrid-row": {
                  transition: 'background-color 0.15s ease',
                  "&:nth-of-type(even)": {
                      backgroundColor: '#fafbfc',
                  },
                  "&:hover": {
                      backgroundColor: '#f1f5f9 !important',
                  },
                  "&.Mui-selected": {
                      backgroundColor: '#ebf5fa !important',
                      "&:hover": {
                          backgroundColor: '#dbeef7 !important',
                      },
                  },
              },
              "& .MuiDataGrid-cell": {
                  borderBottom: '1px solid #f1f5f9',
                  fontSize: '14px',
                  color: '#334155',
                  "&:focus": { outline: 'none' },
              },
              "& .MuiDataGrid-virtualScroller": {
                  border: 'none',
              },
              "& .MuiDataGrid-main": {
                  border: 'none',
              },
              "& .MuiDataGrid-footerContainer": {
                  borderTop: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc',
                  minHeight: 56,
              },
              "& .MuiTablePagination-root": {
                  color: '#64748b',
                  fontSize: '13px',
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  marginBottom: 0,
              },
              "& .MuiTablePagination-select": {
                  fontSize: '13px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
              },
              "& .MuiIconButton-root": {
                  color: '#64748b',
                  "&:hover": {
                      backgroundColor: '#f1f5f9',
                      color: '#475569',
                  },
              },
              "& .MuiCheckbox-root": {
                  color: '#94a3b8',
                  "&.Mui-checked": {
                      color: '#2099d2',
                  },
              },
          }}
          // experimentalFeatures={{ newEditingApi: true }}
          />
        </div>
      </div>
    </>
  )
}
